import React, { useState, useEffect } from "react";
import "./Overview.css";
import { Download } from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";

const Overview = () => {
  const superAdminId = localStorage.getItem("adminuserId");
  const [branches, setBranches] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cashData, setCashData] = useState(null);
  const [bankData, setBankData] = useState(null);

  // Filter State
  const [filters, setFilters] = useState({
    branchId: "",
    startDate: "",
    endDate: "",
    ledgerId: "",
  });

  // Fetch Branches
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await axios.get("https://payyug.in/api/get-branch", {
          params: {
            SuperAdminId: superAdminId,
            limit: "all",
          },
        });
        setBranches(res.data.branches || []);
      } catch (err) {
        console.error("Fetch Branches Error:", err);
      }
    };
    fetchBranches();
  }, []);

  // Fetch Ledgers
  useEffect(() => {
    const fetchLedgers = async () => {
      try {
        const res = await axios.get(
          `https://payyug.in/api/ledger/getall`,
          {
            params: {
              superAdminId,
            },
          }
        );
        setLedgers(res.data.rows || []);
      } catch (err) {
        console.error("Fetch Ledgers Error:", err);
      }
    };
    fetchLedgers();
  }, []);

  // Handle Filter Change
  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  // Fetch Records
  const handleGetRecords = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Cash book params
      const cashParams = {
        superAdminId,
      };
      if (filters.branchId) cashParams.branchId = filters.branchId;
      if (filters.startDate) cashParams.fromDate = filters.startDate;
      if (filters.endDate) cashParams.toDate = filters.endDate;
      if (filters.ledgerId) cashParams.cashLedgerId = filters.ledgerId;

      // Fetch cash records
      const cashRes = await axios.get(
        "https://payyug.in/api/report/cash-book",
        { params: cashParams }
      );
      setCashData(cashRes.data);

      // Bank book params
      const bankParams = {
        superAdminId,
      };
      if (filters.ledgerId) bankParams.bankLedgerId = filters.ledgerId;
      if (filters.startDate) bankParams.fromDate = filters.startDate;
      if (filters.endDate) bankParams.toDate = filters.endDate;

      // Fetch bank records
      try {
        const bankRes = await axios.get(
          "https://payyug.in/api/report/bank-book",
          { params: bankParams }
        );
        setBankData(bankRes.data);
      } catch (bankErr) {
        console.error("Bank book fetch failed:", bankErr);
        // Continue even if bank book fails
      }

      toast.success("Records loaded successfully");
    } catch (err) {
      console.error("Fetch Error:", err);
      toast.error("Failed to fetch records");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="overview-container">
        <h1 className="overview-heading">Overview</h1>

        {/* Overview Form Section */}
        <section className="overview-top">
          <form className="overview-form" onSubmit={handleGetRecords}>
            <div className="overview-form-section">
              <div className="overview-form-input-fields">
                <label>Select Branch</label>
                <select
                  name="branchId"
                  value={filters.branchId}
                  onChange={handleFilterChange}
                >
                  <option value="">Select Branch</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="overview-form-input-fields">
                <label>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="overview-form-input-fields">
                <label>End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="overview-form-input-fields">
                <label>Select Bank Ledger</label>
                <select
                  name="ledgerId"
                  value={filters.ledgerId}
                  onChange={handleFilterChange}
                >
                  <option value="">Select Bank Ledger</option>
                  {ledgers.map((ledger) => (
                    <option key={ledger.id} value={ledger.id}>
                      {ledger.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="overview-form-submit-btn">
              <button type="submit" disabled={loading}>
                {loading ? "Loading..." : "Get Records"}
              </button>
            </div>
          </form>
        </section>

        {/* pdf download buttons */}
        <div className="overview-pdf-download-btns">
          <button>
            <Download size={15}/> Print Cashbook
          </button>
          <button>
            <Download size={15}/> Print Bankbook
          </button>
          <button>
            <Download size={15}/> Print Cashbook & Bankbook
          </button>
        </div>

        {/* Overview Report Section */}
        <section className="overview-report">
          <div className="overview-report-container">
            <p className="overview-report-heading">Cash Records</p>
            <div className="overview-report-table">
              {cashData ? (
                <div className="overview-report-table-div">
                  <div className="overview-report-debit-table">
                    <p>Debit</p>
                    <table>
                      <thead>
                        <tr>
                          <th>Particulars</th>
                          <th>DR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cashData.debit && cashData.debit.length > 0 ? (
                          cashData.debit.map((item, idx) => (
                            <tr key={idx}>
                              <td>{item.particulars || "N/A"}</td>
                              <td>{item.amount || 0}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="2" style={{ textAlign: "center" }}>
                              No debit records
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="overview-report-credit-table">
                    <p>Credit</p>
                    <table>
                      <thead>
                        <tr>
                          <th>Particulars</th>
                          <th>CR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cashData.credit && cashData.credit.length > 0 ? (
                          cashData.credit.map((item, idx) => (
                            <tr key={idx}>
                              <td>{item.particulars || "N/A"}</td>
                              <td>{item.amount || 0}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="2" style={{ textAlign: "center" }}>
                              No credit records
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p style={{ textAlign: "center", padding: "20px" }}>
                  No cash data available. Apply filters to load data.
                </p>
              )}
              {cashData && (
                <p style={{ textAlign: "center", fontWeight: 600 }}>
                  Closing Balance {cashData.closingBalance || 0}
                </p>
              )}
            </div>
          </div>
          <div className="overview-report-container">
            <p className="overview-report-heading">Bank Records</p>
            <div className="overview-report-table">
              {bankData ? (
                <div className="overview-report-table-div">
                  <div className="overview-report-debit-table">
                    <p>Debit</p>
                    <table>
                      <thead>
                        <tr>
                          <th>Particulars</th>
                          <th>DR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bankData.debit && bankData.debit.length > 0 ? (
                          bankData.debit.map((item, idx) => (
                            <tr key={idx}>
                              <td>{item.particulars || "N/A"}</td>
                              <td>{item.amount || 0}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="2" style={{ textAlign: "center" }}>
                              No debit records
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="overview-report-credit-table">
                    <p>Credit</p>
                    <table>
                      <thead>
                        <tr>
                          <th>Particulars</th>
                          <th>CR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bankData.credit && bankData.credit.length > 0 ? (
                          bankData.credit.map((item, idx) => (
                            <tr key={idx}>
                              <td>{item.particulars || "N/A"}</td>
                              <td>{item.amount || 0}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="2" style={{ textAlign: "center" }}>
                              No credit records
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p style={{ textAlign: "center", padding: "20px" }}>
                  No bank data available. Apply filters to load data.
                </p>
              )}
              {bankData && (
                <p style={{ textAlign: "center", fontWeight: 600 }}>
                  Closing Balance {bankData.closingBalance || 0}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Overview Bottom Section */}
        <section className="overview-bottom">
          <h1 className="profit-loss-heading">Profit-Loss</h1>
          <div className="overview-bottom-grid">
            <div className="overview-bottom-info-div">
              <h3>Income</h3>
              <p>
                Indirect Income: <span>0</span>
              </p>
            </div>
            <div className="overview-bottom-info-div">
              <h3>Expense</h3>
              <p>
                Indirect Expense: <span>0</span>
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Overview;
